/**
 * How well we keep our promises, and what is going quiet. Used by Today and the Insights page.
 *   - Replies within 2 working days of an enquiry (the website's promise)
 *   - Audit reports within 5 working days of complete data (the website's promise)
 *   - Implementation within 6 weeks of kickoff
 *   - Leads with no contact for a while ("going stale", the idea CRMs call rotting deals)
 * Pure functions: every date is passed in, so the tests can pin them.
 */
import type { AuditRow, ReportRow } from "../data/db.ts";
import { isOpen, type LeadRow } from "./leads.ts";
import { londonDate } from "./working-days.ts";

/** A lead with no contact for this many days is going stale (prospects in a sequence and new enquiries have their own timers). */
export const STALE_DAYS = 14;
/** How far back the on-time rates look. */
export const LOOKBACK_DAYS = 90;

const DAY = 864e5;
const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);

export interface Promise_ {
  /** Promises that fell due in the period (kept or not) */
  due: number;
  onTime: number;
  late: number;
  /** Not yet due, still open */
  pending: number;
  /** Share kept, 0 to 1, or null when nothing has fallen due */
  rate: number | null;
}

const rate = (onTime: number, due: number) => (due ? onTime / due : null);

/** Replies to website enquiries, partner introductions and inbound leads, against their reply-by time. */
export function replyPromise(leads: LeadRow[], now = new Date(), days = LOOKBACK_DAYS): Promise_ & { medianHours: number | null } {
  const from = now.getTime() - days * DAY;
  let due = 0, onTime = 0, late = 0, pending = 0;
  const hours: number[] = [];
  for (const l of leads) {
    if (l.origin === "outbound" || !l.reply_due || ms(l.received_at) < from) continue;
    const by = ms(l.reply_due);
    if (l.replied_at) {
      due++;
      if (ms(l.replied_at) <= by) onTime++; else late++;
      hours.push((ms(l.replied_at) - ms(l.received_at)) / 36e5);
    } else if (now.getTime() > by && isOpen(l.status)) { due++; late++; }
    else if (isOpen(l.status)) pending++;
  }
  hours.sort((a, b) => a - b);
  const medianHours = hours.length ? hours[Math.floor((hours.length - 1) / 2)]! : null;
  return { due, onTime, late, pending, rate: rate(onTime, due), medianHours };
}

/** First draft report against the report due date (5 working days from complete data). */
export function reportPromise(audits: AuditRow[], reports: Map<string, ReportRow[]>, now = new Date(), days = LOOKBACK_DAYS): Promise_ {
  const today = londonDate(now);
  const from = londonDate(new Date(now.getTime() - days * DAY));
  let due = 0, onTime = 0, late = 0, pending = 0;
  for (const a of audits) {
    if (!a.report_due || a.report_due < from) continue;
    const first = (reports.get(a.id) ?? []).map((r) => londonDate(new Date(r.created_at))).sort()[0];
    if (first) { due++; if (first <= a.report_due) onTime++; else late++; }
    else if (a.report_due < today) { due++; late++; }
    else pending++;
  }
  return { due, onTime, late, pending, rate: rate(onTime, due) };
}

/** Implementations still running past their end date. */
export function implementationsOverrun(audits: AuditRow[], now = new Date()): AuditRow[] {
  const today = londonDate(now);
  return audits.filter((a) => a.status === "implementing" && a.implementation_ends && a.implementation_ends < today);
}

/** The last time we heard from or contacted a lead. */
export function lastActivity(l: LeadRow): string {
  return [l.last_contact_at, l.last_touch_at, l.replied_at, l.call_done_at, l.received_at, l.created_at].filter((x): x is string => !!x).sort().pop() ?? l.created_at;
}

/** Open leads in conversation with no contact for STALE_DAYS or more, longest quiet first. */
export function staleLeads(leads: LeadRow[], now = new Date(), days = STALE_DAYS): { lead: LeadRow; quietDays: number }[] {
  return leads
    .filter((l) => isOpen(l.status) && !l.do_not_contact && !["new", "prospect", "contacted"].includes(l.status))
    .map((lead) => ({ lead, quietDays: Math.floor((now.getTime() - ms(lastActivity(lead))) / DAY) }))
    .filter((x) => x.quietDays >= days)
    .sort((a, b) => b.quietDays - a.quietDays);
}

/** Clients won this month. */
export function clientsThisMonth(leads: LeadRow[], now = new Date()): { won: number; month: string } {
  const month = londonDate(now).slice(0, 7);
  const won = leads.filter((l) => l.won_at && londonDate(new Date(l.won_at)).slice(0, 7) === month).length;
  return { won, month };
}

/** Where leads come from and how far they get: leads, calls held, clients. */
export function sourceFunnel(leads: LeadRow[]): { origin: LeadRow["origin"]; leads: number; calls: number; clients: number }[] {
  return (["inbound", "outbound", "partner"] as const).map((origin) => {
    const mine = leads.filter((l) => l.origin === origin);
    return { origin, leads: mine.length, calls: mine.filter((l) => l.call_done_at || l.status === "won").length, clients: mine.filter((l) => l.status === "won").length };
  });
}

export const pctText = (r: number | null): string => (r === null ? "–" : `${Math.round(r * 100)}%`);
